import { useEffect, useState } from 'react';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ParentCommunityPost } from './ParentCommunityPost';

interface Post {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  archived?: boolean;
  archived_at?: string;
  archived_reason?: string;
  author: {
    student_name: string;
    avatar_url: string | null;
  };
  comments: Comment[];
}

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  archived?: boolean;
  archived_at?: string;
  author: {
    student_name: string;
    avatar_url: string | null;
  };
}

export const ParentCommunityFeed = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchPosts = async () => {
    try {
      if (!user?.id) {
        throw new Error('No user found');
      }

      console.log('Fetching posts for user:', user.id);

      // Obtener todos los posts del usuario usando la función RPC
      const { data: posts, error: postsError } = await supabase
        .rpc('get_user_posts_for_parent', {
          p_user_id: user.id
        });

      if (postsError) {
        console.error('Error fetching posts:', postsError);
        throw postsError;
      }

      // Obtener los comentarios para cada post
      const postsWithComments = await Promise.all(
        (posts || []).map(async (post) => {
          const { data: comments, error: commentsError } = await supabase
            .from('post_comments')
            .select(`
              id,
              content,
              created_at,
              archived,
              archived_at,
              user_id,
              author:profiles (
                student_name,
                avatar_url
              )
            `)
            .eq('post_id', post.id)
            .order('created_at', { ascending: true });

          if (commentsError) {
            console.error('Error fetching comments:', commentsError);
            return { ...post, comments: [] };
          }

          return {
            ...post,
            comments: comments || []
          };
        })
      );

      console.log('Posts with comments:', {
        total: postsWithComments.length,
        archived: postsWithComments.filter(p => p.archived).length,
        active: postsWithComments.filter(p => !p.archived).length,
        sample: postsWithComments.slice(0, 2).map(p => ({
          id: p.id,
          content: p.content.substring(0, 20),
          archived: p.archived,
          commentsCount: p.comments.length
        }))
      });

      setPosts(postsWithComments);
    } catch (error) {
      console.error('Error in fetchPosts:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudieron cargar las publicaciones. Por favor, intenta de nuevo.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [user?.id]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No hay publicaciones para mostrar</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <ParentCommunityPost
          key={post.id}
          {...post}
        />
      ))}
    </div>
  );
};
